<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { isAxiosError } from 'axios';
import { Info, Send } from 'lucide-vue-next';
import { ref, watch } from 'vue';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { DATE_FORMATS } from '~/constants';
import date from '~/utils/date';

import backingService, { type BackingResponse } from '../services/backingService';

interface ResultWithRequestType extends BackingResponse {
  requestType: 'csv' | 'individual';
  timestamp: Date;
}

const INTERMEDIADOR_OPTIONS = [
  { name: 'Solfácil', value: 'solfacil' },
  { name: 'Kanastra', value: 'kanastra' },
  { name: 'Access', value: 'access' },
  { name: 'JGP', value: 'jgp' },
] as const;

const requestTypeModel = ref<'csv' | 'individual'>('individual');
const intermediadorModel = ref<string | undefined>(undefined);
const financingIdModel = ref<number | undefined>(undefined);
const csvFileModel = ref<File | undefined>(undefined);
const financingIdError = ref<string | undefined>(undefined);
const csvIds = ref<number[]>([]);
const csvError = ref<string | undefined>(undefined);
const intermediadorError = ref<string | undefined>(undefined);
const results = ref<ResultWithRequestType[]>([]);

function clearCsvError(): void {
  csvError.value = undefined;
}

function clearFieldsByRequestType(): void {
  if (requestTypeModel.value === 'individual') {
    csvFileModel.value = undefined;
    csvIds.value = [];
    csvError.value = undefined;
    const fileInput = document.getElementById('csv_file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  } else {
    financingIdModel.value = undefined;
    financingIdError.value = undefined;
  }
}

function clearFinancingIdError(): void {
  financingIdError.value = undefined;
}

function clearForm(): void {
  intermediadorModel.value = undefined;
  financingIdModel.value = undefined;
  csvFileModel.value = undefined;
  csvIds.value = [];
  financingIdError.value = undefined;
  csvError.value = undefined;
  intermediadorError.value = undefined;

  const fileInput = document.getElementById('csv_file') as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
}

function clearIntermediadorError(): void {
  intermediadorError.value = undefined;
}

async function handleFileChange(event: Event): Promise<void> {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  csvFileModel.value = file;
  csvIds.value = [];
  clearCsvError();

  if (!file) return;

  try {
    const text = await file.text();
    const lines = text.split('\n').map((line) => line.trim()).filter((line) => line !== '');

    const ids = lines.map((line) => {
      const value = line.split(',')[0].trim();
      return Number.parseInt(value, 10);
    });

    const invalidIndex = ids.findIndex((id) => Number.isNaN(id));
    if (invalidIndex !== -1) {
      const invalidValue = lines[invalidIndex].split(',')[0].trim();
      csvError.value = `Valor inválido encontrado: "${invalidValue}". Apenas números são permitidos.`;
      csvIds.value = [];
      return;
    }

    if (ids.length === 0) {
      csvError.value = 'O arquivo CSV está vazio ou não contém IDs válidos.';
      return;
    }

    csvIds.value = ids;
  } catch (error) {
    csvError.value = 'Erro ao ler o arquivo CSV. Verifique se o arquivo está no formato correto.';
  }
}

const isSubmitting = ref(false);

watch(requestTypeModel, () => {
  clearFieldsByRequestType();
});

async function handleSubmit(): Promise<void> {
  intermediadorError.value = undefined;
  financingIdError.value = undefined;
  csvError.value = undefined;

  if (!intermediadorModel.value) {
    intermediadorError.value = 'O campo intermediador é obrigatório';
    return;
  }

  if (requestTypeModel.value === 'individual') {
    if (!financingIdModel.value) {
      financingIdError.value = 'O campo ID do financiamento é obrigatório';
      return;
    }

    try {
      isSubmitting.value = true;
      const response = await backingService.sendIndividual({
        backing_id: financingIdModel.value,
        intermediator: intermediadorModel.value,
      });
      results.value.push({
        ...response,
        requestType: 'individual',
        timestamp: new Date(),
      });
      clearForm();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        financingIdError.value = 'ID não foi encontrado';
      } else {
        financingIdError.value = 'Erro ao enviar lastro. Tente novamente.';
      }
    } finally {
      isSubmitting.value = false;
    }
  } else if (requestTypeModel.value === 'csv') {
    if (!csvFileModel.value) {
      csvError.value = 'O campo arquivo CSV é obrigatório';
      return;
    }

    if (csvIds.value.length === 0) {
      csvError.value = 'O arquivo CSV deve conter pelo menos um ID válido';
      return;
    }

    try {
      isSubmitting.value = true;
      const response = await backingService.sendBulk({
        ids: csvIds.value,
        intermediator: intermediadorModel.value,
      });
      results.value.push({
        ...response,
        requestType: 'csv',
        timestamp: new Date(),
      });
      clearForm();
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 404) {
          csvError.value = 'Um ou mais IDs não foram encontrados.';
        } else if (error.response?.status === 400) {
          csvError.value = 'Dados inválidos. Verifique os IDs no arquivo CSV.';
        } else {
          csvError.value = 'Erro ao enviar lastros. Tente novamente.';
        }
      } else {
        csvError.value = 'Erro ao enviar lastros. Tente novamente.';
      }
    } finally {
      isSubmitting.value = false;
    }
  }
}
</script>

<template>
  <DashboardLayout title="Lastro" description="Solicitar Envio de Lastro">
    <div class="row row-deck">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title">Enviar Lastro</h2>
            <div class="mb-3">
              <div class="row">
                <div class="mb-3 col-12 col-md-6">
                  <label class="form-label" for="intermediador">
                    Intermediador <span class="text-danger">*</span>
                  </label>
                  <select
                    id="intermediador"
                    v-model="intermediadorModel"
                    class="form-select"
                    :class="{ 'is-invalid': intermediadorError }"
                    aria-label="Campo de seleção de intermediador"
                    @change="clearIntermediadorError"
                  >
                    <option disabled :value="undefined">Selecione...</option>
                    <option
                      v-for="option in INTERMEDIADOR_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                  <div v-if="intermediadorError" class="invalid-feedback d-block">
                    {{ intermediadorError }}
                  </div>
                </div>
                <div class="mb-3 col-12 col-md-6">
                  <label class="form-label mb-3">Selecione o tipo de solicitação:</label>
                  <div class="d-flex flex-column flex-md-row gap-2">
                    <label class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="radios-inline"
                        value="individual"
                        v-model="requestTypeModel"
                      />
                      <span class="form-check-label">Solicitação Individual</span>
                    </label>
                    <label class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="radios-inline"
                        value="csv"
                        v-model="requestTypeModel"
                      />
                      <span class="form-check-label">Solicitação por CSV</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div v-if="requestTypeModel === 'individual'" class="mb-3 col-12">
                <label class="form-label" for="financing_id">
                  ID do financiamento <span class="text-danger">*</span>
                </label>
                <input
                  id="financing_id"
                  v-model.number="financingIdModel"
                  type="number"
                  class="form-control financing-id-input"
                  :class="{ 'is-invalid': financingIdError }"
                  aria-label="Campo numérico de ID do financiamento"
                  placeholder="Digite o ID do financiamento"
                  @input="clearFinancingIdError"
                />
                <div v-if="financingIdError" class="invalid-feedback d-block">
                  {{ financingIdError }}
                </div>
                <div v-if="!financingIdError" class="mt-2 d-flex align-items-start gap-2 text-muted">
                  <Info class="icon icon-sm mt-1 flex-shrink-0" />
                  <small>Informe o ID numérico do financiamento para solicitar o envio do lastro</small>
                </div>
              </div>
              <div v-if="requestTypeModel === 'csv'" class="mb-3 col-12">
                <label class="form-label" for="csv_file">
                  Arquivo CSV <span class="text-danger">*</span>
                </label>
                <div class="custom-file-input-wrapper">
                  <input
                    id="csv_file"
                    type="file"
                    accept=".csv"
                    class="custom-file-input"
                    :class="{ 'is-invalid': csvError }"
                    aria-label="Campo de upload de arquivo CSV"
                    @change="handleFileChange"
                  />
                  <label for="csv_file" class="custom-file-label">
                    Selecionar arquivo
                  </label>
                  <span class="custom-file-name">
                    {{ csvFileModel?.name || 'Nenhum arquivo selecionado' }}
                  </span>
                </div>
                <div v-if="csvError" class="invalid-feedback d-block">
                  {{ csvError }}
                </div>
                <div v-if="!csvError && csvIds.length > 0" class="mt-3">
                  <div class="p-3 bg-success-lt rounded">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <Info class="icon icon-sm flex-shrink-0" />
                      <strong class="small">Quantidade de IDs encontrados no arquivo: {{ csvIds.length }}</strong>
                    </div>
                  </div>
                </div>
                <div v-if="!csvError && csvIds.length === 0" class="mt-3 p-3 bg-blue-lt rounded">
                  <div class="d-flex align-items-start gap-2 mb-2">
                    <Info class="icon icon-sm mt-1 flex-shrink-0" />
                    <strong class="small">Formato do CSV:</strong>
                  </div>
                  <ul class="mb-0 small text-muted" style="list-style-type: disc; padding-left: 1.5rem;">
                    <li>Uma coluna: ID do lastro (número)</li>
                    <li>Um ID por linha: Sem cabeçalho</li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end mt-4 submit-button-container">
              <button
                type="button"
                class="btn btn-primary d-flex align-items-center gap-2"
                :disabled="isSubmitting"
                @click="handleSubmit"
              >
                <span
                  v-if="isSubmitting"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                <Send v-else class="icon icon-sm" />
                {{ isSubmitting ? 'Enviando...' : 'Enviar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="results.length > 0" class="col-12 mt-4">
        <div class="card">
          <div class="card-body">
            <h3 class="card-title mb-4">Resultados das Solicitações</h3>
            <div
              v-for="(result, index) in [...results].reverse()"
              :key="result.timestamp.getTime()"
              class="mb-4"
              :class="{ 'border-top pt-4': index > 0 }"
            >
              <div class="d-flex justify-content-between align-items-center mb-3">
                <small class="text-muted">
                  {{ date(result.timestamp).format(DATE_FORMATS.DATETIME) }}
                </small>
              </div>
              <div class="row">
                <div class="col-12 col-md-4 mb-3">
                  <div class="p-3 bg-light rounded">
                    <div class="small text-muted mb-1">Tipo de solicitação</div>
                    <div class="fw-bold">
                      {{ result.requestType === 'individual' ? 'Solicitação Individual' : 'Solicitação por CSV' }}
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="p-3 bg-light rounded">
                    <div class="small text-muted mb-1">Total Processado</div>
                    <div class="fw-bold">{{ result.total_processed }}</div>
                  </div>
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <div class="p-3 bg-light rounded">
                    <div class="small text-muted mb-1">Total Agendado</div>
                    <div class="fw-bold">{{ result.total_sent }}</div>
                  </div>
                </div>
              </div>
              <div v-if="result.not_found && result.not_found.length > 0" class="row mt-3">
                <div class="col-12">
                  <div class="p-3 bg-warning-lt rounded">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <Info class="icon icon-sm flex-shrink-0" />
                      <strong class="small">IDs não encontrados: {{ result.not_found.length }}</strong>
                    </div>
                    <div class="small text-muted">
                      <div class="mb-2">Os seguintes IDs não foram encontrados:</div>
                      <div class="d-flex flex-wrap gap-1">
                        <span
                          v-for="(id, idIndex) in result.not_found"
                          :key="idIndex"
                          class="badge bg-warning text-dark"
                        >
                          {{ id }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>

</template>

<style scoped lang="scss">
// Responsive breakpoints
$mobile: 576px;
$tablet: 768px;
$desktop: 992px;

.financing-id-input {
  /* Remove os spinners do input type="number" */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  /* Remove os spinners no Firefox */
  -moz-appearance: textfield;
  appearance: textfield;
}

// Remove ícones de validação (X) dos campos com erro
.form-control.is-invalid,
.form-select.is-invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;

  &::after {
    display: none !important;
  }
}

// Remove ícones de validação do input file
input[type="file"].is-invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}

// Customização do input de arquivo
.custom-file-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.custom-file-input {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.custom-file-label {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  background-color: #0d6efd;
  border: 1px solid #0d6efd;
  border-radius: 0.375rem;
  cursor: pointer;
  color: #fff;
  font-size: 1rem;
  line-height: 1.5;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
  white-space: nowrap;

  &:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
  }

  &:active {
    background-color: #0a58ca;
    border-color: #0a53be;
  }
}

.custom-file-name {
  color: #6c757d;
  font-size: 0.875rem;
  flex: 1;
  min-width: 200px;
}

.custom-file-input-wrapper:has(.is-invalid) .custom-file-label {
  border-color: #dc3545;
  background-color: #dc3545;
}

.custom-file-input-wrapper:has(.is-invalid) .custom-file-name {
  color: #dc3545;
}

.card {
  .card-body {
    @media (max-width: $tablet) {
      padding: 1rem;
    }
  }

  .card-title {
    @media (max-width: $mobile) {
      font-size: 1.25rem;
    }
  }
}

// Ajustes para textos informativos em mobile
.text-muted {
  small {
    @media (max-width: $mobile) {
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }
}

// Ajustes para badges de IDs em mobile
.badge {
  @media (max-width: $mobile) {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }
}

// Ajustes para áreas de informação
.bg-success-lt,
.bg-blue-lt,
.bg-warning-lt {
  @media (max-width: $mobile) {
    padding: 0.75rem !important;
  }
}

// Ajustes para o botão de envio
.submit-button-container {
  @media (max-width: $tablet) {
    width: 100%;
    justify-content: stretch !important;

    .btn {
      width: 100%;
    }
  }
}
</style>
